import React, { useEffect, useRef, useState } from 'react';

/**
 * Typewriter — reveals styled text segments character-by-character with a
 * blinking cursor. Supports either a single `segments` sequence or a
 * `sequences` array that the component will type, pause, erase, and cycle
 * through indefinitely (classic rotating headline pattern).
 *
 * Props:
 *   - segments:    Array<{ text, className? }>            — one-shot mode
 *   - sequences:   Array< Array<{ text, className? }> >   — rotating mode
 *   - speed:       ms per typed character (default 32)
 *   - eraseSpeed:  ms per erased character (default 18)
 *   - holdMs:      ms to pause after a sequence finishes typing (default 1800)
 *   - startDelay:  ms before the very first character is typed
 *   - onDone:      called once the first sequence finishes typing
 *   - showCursor:  boolean (default true)
 */
export const Typewriter = ({
  segments,
  sequences,
  speed = 32,
  eraseSpeed = 18,
  holdMs = 1800,
  startDelay = 0,
  onDone,
  showCursor = true,
  className = '',
  cursorClassName = '',
  as: Tag = 'span',
}) => {
  const rotating = Array.isArray(sequences) && sequences.length > 0;
  const list = rotating ? sequences : [segments || []];

  const [seqIdx, setSeqIdx] = useState(0);
  const [count, setCount] = useState(0);      // characters shown from current sequence
  const [phase, setPhase] = useState('idle'); // idle | typing | holding | erasing
  const timer = useRef(null);
  const onceDone = useRef(false);

  const currentSeq = list[seqIdx] || [];
  const totalChars = currentSeq.reduce((n, s) => n + s.text.length, 0);

  useEffect(() => {
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setCount(totalChars);
      setPhase('holding');
      if (!onceDone.current && onDone) { onDone(); onceDone.current = true; }
      return;
    }
    // Kick off the very first typing pass after startDelay
    const t = window.setTimeout(() => setPhase('typing'), startDelay);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase === 'idle') return;

    if (phase === 'typing') {
      if (count < totalChars) {
        timer.current = window.setTimeout(() => setCount((c) => c + 1), speed);
      } else {
        if (!onceDone.current && onDone) { onDone(); onceDone.current = true; }
        if (rotating && list.length > 1) {
          timer.current = window.setTimeout(() => setPhase('erasing'), holdMs);
        } else {
          setPhase('holding');
        }
      }
    } else if (phase === 'erasing') {
      if (count > 0) {
        timer.current = window.setTimeout(() => setCount((c) => c - 1), eraseSpeed);
      } else {
        // Advance to the next sequence and start typing again
        setSeqIdx((i) => (i + 1) % list.length);
        setPhase('typing');
      }
    }

    return () => window.clearTimeout(timer.current);
  }, [phase, count, seqIdx]);

  // Slice each segment of the current sequence based on the number of
  // characters currently revealed.
  let remaining = count;
  const rendered = currentSeq.map((seg, i) => {
    if (remaining <= 0) return null;
    const shown = seg.text.slice(0, remaining);
    remaining -= seg.text.length;
    return (
      <span key={i} className={seg.className || undefined}>
        {shown}
      </span>
    );
  });

  const cursorBlink = phase === 'holding' || phase === 'idle';
  return (
    <Tag className={className}>
      {rendered}
      {showCursor && (
        <span
          aria-hidden='true'
          className={`k9-typewriter-cursor ${cursorBlink ? 'k9-typewriter-cursor--blink' : ''} ${cursorClassName}`}
        >
          |
        </span>
      )}
    </Tag>
  );
};

export default Typewriter;
